import { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import api from "api/api";
 
// mui component
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import ReactSelectStyles from "../../../styles/ReactSelectStyles";
 
// mui icon
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
 
//import component
import Statements from "../../claimsMapping/Statements";
import Tags from "../../claimsMapping/Tags";
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "store/features/notificationSlice";

// Import utility functions from studyTypeUtils
import {
    transformStudyTypeOptions,
    transformStudyTypeMethodOptions,
    formatStudyTypeLabel,
    formatMethodLabel,
    getStudyTypeMethodSelectStyles,
    getInfoMessageOption,
    handleTemplateBasedMethodSelection
} from "../../../utils/studyTypeUtils";

function ClaimsMapping() {
    const [tab, setTab] = useState("statement");
    const [studyTypeOptions, setStudyTypeOptions] = useState([]);
    const [studyTypeMethodOptions, setStudyTypeMethodOptions] = useState([]);
    const [selectedStudyType, setSelectedStudyType] = useState(null);
    const [selectedStudyTypeMethod, setSelectedStudyTypeMethod] = useState([]);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const dispatch = useDispatch();
 
    const handleChange = (event, newValue) => {
        if (newValue) {
            setTab(newValue);
        }
    };
    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    const optionsWithInfoMessage = useMemo(() => {
        if (studyTypeMethodOptions.length === 0) return [];
        const infoMessage = getInfoMessageOption();
        return [infoMessage, ...studyTypeMethodOptions];
    }, [studyTypeMethodOptions]);

    const customMethodReactSelectStyles = useMemo(
        () => getStudyTypeMethodSelectStyles(ReactSelectStyles),
        []
    );

    useEffect(() => {
        const fetchStudyTypes = async () => {
            try {
                let url = process.env.REACT_APP_SERVER_PATH + `/study-types/all/`;
                if (productCategoryId) url += `?product_category=${productCategoryId}`;
                const res = await api.get(url);
                const options = transformStudyTypeOptions(res.data);
                setStudyTypeOptions(options);
            } catch (error) {
                handleError("error", error);
            }
        };
        fetchStudyTypes();
    }, [productCategoryId]);

    // Fetch study type methods when study type changes
    useEffect(() => {
        const fetchStudyTypeMethods = async () => {
            try {
                let url = process.env.REACT_APP_SERVER_PATH + `/study-type-methods/all/`;
                if (productCategoryId) url += `?product_category=${productCategoryId}`;
                if (selectedStudyType?.value) url += `&study_type=${selectedStudyType.value}`;
                const res = await api.get(url);
                const options = transformStudyTypeMethodOptions(res.data);
                setStudyTypeMethodOptions(options);
            } catch (error) {
                handleError("error", error);
            }
        };
        fetchStudyTypeMethods();
    }, [selectedStudyType, productCategoryId]);

    // Handle study type change
    const handleStudyTypeChange = (selectedValue) => {
        setSelectedStudyType(selectedValue);
        if (selectedValue && (!selectedStudyTypeMethod.length || selectedStudyTypeMethod[0]?.studyTypeValue !== selectedValue.value)) {
            setSelectedStudyTypeMethod([]);
        }
    };
    const handleStudyTypeMethodChange = (newValue, actionMeta) => {
        const updatedSelection = handleTemplateBasedMethodSelection(
            selectedStudyTypeMethod,
            actionMeta,
            studyTypeMethodOptions
        );
        setSelectedStudyTypeMethod(updatedSelection);
    };

    return (
        <>
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "10px",
                }}
            >
                <div style={{ display: "flex", gap: "10px", alignItems: "center", marginRight: "16px", flexWrap: "wrap" }}>
                    <div style={{ width: "200px" }}>
                        <Select
                            options={studyTypeOptions}
                            value={selectedStudyType}
                            onChange={handleStudyTypeChange}
                            formatOptionLabel={formatStudyTypeLabel}
                            placeholder="Study Type"
                            isClearable
                            styles={ReactSelectStyles}
                        />
                    </div>
                    <div style={{ width: "300px", minWidth: "400px" }}>
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={optionsWithInfoMessage}
                            value={selectedStudyTypeMethod}
                            onChange={handleStudyTypeMethodChange}
                            formatOptionLabel={formatMethodLabel}
                            placeholder="Study Type Method"
                            isClearable
                            isDisabled={!selectedStudyType}
                            styles={customMethodReactSelectStyles}
                        />
                    </div>
                </div>
                <div
                    style={{
                        zIndex: 2,
                        position: "relative",
                    }}
                >
                    <ToggleButtonGroup
                        color="primary"
                        value={tab}
                        exclusive
                        onChange={handleChange}
                        aria-label="Platform"
                        size="small"
                    >
                        <ToggleButton value="statement">
                            <Tooltip
                                title="Claims"
                                placement="top"
                            >
                                <TextSnippetIcon
                                    sx={{
                                        fontSize: "16px",
                                    }}
                                />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="tag">
                            <Tooltip
                                title="Tag"
                                placement="top"
                            >
                                <WorkspacesIcon
                                    sx={{
                                        fontSize: "16px",
                                    }}
                                />
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </div>
            </div>
            <div
                style={{
                    marginTop: 10,
                }}
            >
                {tab === "statement" && (
                    <Statements
                        selectedStudyType={selectedStudyType}
                        selectedStudyTypeMethod={selectedStudyTypeMethod}
                    />
                )}
                {tab === "tag" && (
                    <Tags
                        selectedStudyType={selectedStudyType}
                        selectedStudyTypeMethod={selectedStudyTypeMethod}
                    />
                )}
            </div>
        </>
    );
}
 
export default ClaimsMapping;
